import {
  type Locale as MelwaysLocale,
  siteNames,
  type Language,
} from '@seek/melways-sites';
import { types, type Instance, type IAnyModelType } from 'mobx-state-tree';

import type { PageConfig } from 'src/libs/PageConfig';
import { sites } from 'src/siteConfig';

import type { QueryParams } from '../../utils/hooks/types';
import JobApplicationStore, {
  type IJobApplicationStore,
} from '../JobApplicationStore';
import type { HighFitApplicationData } from '../JobApplicationStore/types';
import JobStore, { type IJobStore } from '../JobStore';
import UserStore from '../UserStore';
import { configureNegativeCountMonitor } from '../reactions/configureNegativeCountMonitor';

const DEFAULT_POSITION_ID = -1;

const Site = types.union(
  ...Object.values(sites).map((site) => types.literal(site.name)),
);

const locales: MelwaysLocale[] = [
  'en-AU',
  'en-HK',
  'en-ID',
  'en-MY',
  'en-NZ',
  'en-PH',
  'en-SG',
  'en-TH',
  'th-TH',
  'id-ID',
];
const Locale = types.union(
  ...Object.values(locales).map((locale) => types.literal(locale)),
);

const Store = types
  .model('RootStore', {
    site: types.optional(Site, siteNames.employer.seek.au),
    locale: types.optional(types.union(Locale), 'en-AU'),
    isMobile: types.optional(types.boolean, false),
    advertiserId: types.optional(types.string, ''),
    seekAdvertiserId: types.optional(types.string, ''),
    /**
     * @deprecated
     * There seems to be some race condition with this positionId where -1 (DEFAULT_POSITION_ID) is sent to the backend
     */
    positionId: types.optional(types.number, DEFAULT_POSITION_ID),
    /**
     * @deprecated
     * There seems to be some race condition with this jobId where -1 (DEFAULT_POSITION_ID) is sent to the backend
     */
    jobId: types.optional(types.number, DEFAULT_POSITION_ID),
    userStore: types.optional(UserStore, {}),
    jobStore: types.optional(
      types.late((): IAnyModelType => JobStore),
      {
        job: {},
      },
    ),
    jobApplicationStore: types.optional(
      types.late((): IAnyModelType => JobApplicationStore),
      {
        jobApplication: [],
      },
    ),
  })
  .views((self) => ({
    get currentPageJobApplications() {
      return self.jobApplicationStore.currentPageJobApplications;
    },

    get job() {
      return self.jobStore.job;
    },
  }))
  .actions((self) => ({
    async initLoad(
      advertiserId: string,
      seekAdvertiserId: string,
      positionId: number,
      jobId: number,
      queryParams: QueryParams,
      language: Language,
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
      initialMustHaveFilterState:
        | 'selected'
        | 'unselected'
        | undefined = 'selected',
      jobPlacementExperimentVariant:
        | 'not_yet'
        | 'too_early_to_say'
        | 'still_reviewing_applications'
        | 'control',
      highFitApplicationData: HighFitApplicationData,
    ) {
      self.advertiserId = advertiserId;
      self.seekAdvertiserId = seekAdvertiserId;
      self.positionId = positionId;
      self.jobId = jobId;

      await self.jobStore.questionnaire.loadQuestionnaire().then(() => {
        // questionnaire loaded
        const { questionnaire } = self.jobStore;
        if (questionnaire) {
          self.jobApplicationStore.initLoadFilters(questionnaire);
          if (
            !queryParams.selectedProspectId &&
            initialMustHaveFilterState === 'selected' &&
            !questionnaire.autoReject
          ) {
            self.jobApplicationStore.filtersStore.enableMustHaveFilter();
          }
        }
      });

      await (self.jobApplicationStore as IJobApplicationStore).initLoad(
        positionId,
        queryParams,
        language,
        addCustomError,
        jobPlacementExperimentVariant,
        highFitApplicationData,
      );
    },

    setMatchMedia(isMobile: boolean) {
      self.isMobile = isMobile;
    },

    afterCreate() {
      configureNegativeCountMonitor(self as IStore);
    },

    setSeekAdvertiserId(seekAdvertiserId: string) {
      self.seekAdvertiserId = seekAdvertiserId;
    },

    getCandidateCardStateForTracking() {
      // FIXME: This is not best practice because we are duplicating the logic of the hook here and it is not strongly typed. Ideally we use the state value that the React component uses but this function is called by the loadJobApplication function which is called in multiple areas.
      const userActionsFromLocalStorage =
        window.localStorage.getItem('cm-user-actions');

      if (!self.jobStore?.questionnaire?.hasQuestion) {
        return 'No RR';
      }

      if (!userActionsFromLocalStorage) {
        // This is the default value set in UserActionStatusContext
        return 'Expand';
      }
      try {
        const status = JSON.parse(userActionsFromLocalStorage)[
          'application-card-state'
        ][`${self.userStore.user.userId}`];
        const statusMap: Record<string, 'Expand' | 'Collapse'> = {
          expanded: 'Expand',
          collapsed: 'Collapse',
        };
        return statusMap[status];
      } catch (err) {
        return 'No RR';
      }
    },
  }));

export type IStore = Omit<
  Instance<typeof Store>,
  'jobApplicationStore' | 'jobStore'
> & {
  jobApplicationStore: IJobApplicationStore;
  jobStore: IJobStore;
};

export default Store;
